/**
 * Hook for fetching admin dashboard analytics data.
 */

import { useState, useEffect, useCallback } from "react";
import { apiGet } from "../lib/api";

// ----- Types -----

export type TimeRange = "24h" | "7d" | "30d";

export interface DashboardOverview {
  totalUsers: number;
  newUsersToday: number;
  newUsersChange: number;
  activeToday: number;
  activeChange: number;
  avgSessionMinutes: number;
  subscriptionBreakdown: {
    free: number;
    family: number;
    classroom: number;
  };
  mrr: number;
  mrrChange: number;
  recentSignups: Array<{
    id: string;
    email: string;
    createdAt: string;
  }>;
  topDisciplines: Array<{
    name: string;
    percentage: number;
  }>;
  userGrowth: Array<{
    date: string;
    total: number;
    new: number;
  }>;
}

interface DashboardResponse {
  data: DashboardOverview;
  cached: boolean;
}

// ----- Hook -----

export function useAdminDashboard(timeRange: TimeRange = "24h") {
  const [data, setData] = useState<DashboardOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCached, setIsCached] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiGet<DashboardResponse>(
        `/api/admin/analytics/overview?range=${timeRange}`
      );

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (result.data) {
        setData(result.data.data);
        setIsCached(result.data.cached);
      } else {
        throw new Error("No data received");
      }
    } catch (err) {
      console.error("Admin dashboard fetch error:", err);
      setError(err instanceof Error ? err.message : "Failed to load dashboard");
    } finally {
      setIsLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return {
    data,
    isLoading,
    error,
    isCached,
    refetch: fetchDashboardData,
  };
}

export default useAdminDashboard;
